import {
  analytics,
  currentPageUrl,
  identifyUser,
  initAnalytics,
  isAnalyticsEnabled,
  isUserIdentified,
  resetIdentity,
  setPreviousPathname,
  startSessionReplay
} from "@/lib/analytics";
import { ENTER_PATH } from "@/lib/navigation/routes";
import { useProfile } from "@/queries/customer/customer";
import { router } from "@/router";
import { isFetchError } from "@repo/api-sdk";
import { useCallback, useEffect, useRef } from "react";
import type { ReactNode } from "react";

interface AnalyticsProviderProps {
  children: ReactNode;
}

/**
 * Owns the analytics lifecycle: initialises PostHog once, emits a manual
 * `$pageview` on the initial load and on every resolved route navigation, and
 * binds events to the logged-in customer (identify on login, reset when the
 * session is gone).
 *
 * When VITE_POSTHOG_KEY is unset, initAnalytics is a no-op and nothing is
 * captured. That is the default in local runs, but the deployed dev site IS
 * built with a key (deploy-dev.yml's POSTHOG_KEY environment variable), so
 * dev traffic does reach PostHog - do not assume dev events are inert.
 */
export const AnalyticsProvider = ({ children }: AnalyticsProviderProps) => {
  const { data, status, error } = useProfile();
  const customerId = data?.customer?.id;

  // The last URL a pageview was captured for, in the form it is *sent*: origin
  // and pathname only, because `before_send` strips the query string and fragment
  // from every event. Comparing the raw `href` instead let a filter, sort, page
  // or checkout-step change - same page to PostHog - send a repeat pageview with
  // an identical `$current_url` and cut `$prev_pageview_duration` short.
  //
  // In a ref, not in the effect's closure: StrictMode runs the effect, its
  // cleanup and the effect again on mount, and a closure variable would restart
  // empty on that second run and capture the first location twice.
  const lastUrlRef = useRef("");

  // True from mount until the profile settles, when PostHog already holds an
  // identified person from an earlier page load. Their first pageview has to wait:
  // if the session has since expired the person must be reset *before* it, or the
  // anonymous login page is filed under the previous user.
  const holdPageviewsRef = useRef(false);

  const capturePageview = useCallback(() => {
    if (holdPageviewsRef.current) return;
    const url = currentPageUrl();
    if (url === lastUrlRef.current) return;
    lastUrlRef.current = url;
    analytics.pageview();
  }, []);

  useEffect(() => {
    // `/enter` starts analytics itself, once redemption has said who the session
    // belongs to (see `beginConciergeSession`). Initialising here would send
    // `/flags` and a `$pageview` for a session that may turn out to be an
    // operator's, who must make no PostHog calls at all (KAN-40). Its own
    // pageview is captured there too, so mark the location as handled: nothing
    // here may capture it a second time when the router resolves it.
    //
    // This also keeps session replay off, for the same reason: the actor defaults
    // to the salon until redemption says otherwise, so starting here would
    // record the opening seconds of an *operator* session - exactly what the
    // blackout exists to prevent.
    if (window.location.pathname.startsWith(ENTER_PATH)) {
      lastUrlRef.current = currentPageUrl();
    } else {
      initAnalytics();
      holdPageviewsRef.current = isUserIdentified();
      // Dedupe by URL: the initial capture and the router's onResolved can both
      // fire for the same location during the first navigation.
      //
      // Route timing (KAN-44 R3) needs no code here. posthog's `PageViewManager`
      // is driven off the event *name*, not the `capture_pageview` config, so it
      // processes these manual captures too and stamps `$prev_pageview_duration`
      // on each one. An earlier revision computed it here as well; posthog's
      // value overwrote ours on every pageview but the first, and on the one case
      // where the two differed - a session rotation, where posthog deliberately
      // omits the property - ours leaked through and reported the whole idle gap
      // as time on screen.
      capturePageview();

      // Session replay for salon sessions (ADR-0006), gated on a PostHog flag.
      startSessionReplay();
    }

    // Remember the page navigated away from, for events that report where an
    // in-app visitor came from (`not_found_viewed.from_path`). Runs before the
    // destination renders, so the page can read it on mount. Only path changes
    // count: a filter or sort change keeps the same pathname.
    const unsubscribeBeforeLoad = router.subscribe(
      "onBeforeLoad",
      ({ fromLocation, pathChanged }) => {
        if (pathChanged) setPreviousPathname(fromLocation?.pathname);
      }
    );
    const unsubscribeResolved = router.subscribe("onResolved", capturePageview);

    return () => {
      unsubscribeBeforeLoad();
      unsubscribeResolved();
    };
  }, [capturePageview]);

  // Tracks who we last identified so we only identify once per customer.
  const identifiedIdRef = useRef<string | null>(null);

  useEffect(() => {
    // Wait for the profile to settle: until then there is nothing to identify,
    // and "no profile yet" must not read as "logged out".
    if (status === "pending") return;

    if (isAnalyticsEnabled()) {
      if (customerId) {
        // Also when PostHog no longer holds anyone: logout resets it without
        // this ref knowing, and the same customer logging straight back in
        // would otherwise stay anonymous for the whole session.
        if (identifiedIdRef.current !== customerId || !isUserIdentified()) {
          identifiedIdRef.current = customerId;
          // Id only, no person properties: the profile exposes only PII fields
          // (email/phone/name/company), none of which we send to PostHog. When
          // non-identifying segments (region, tier) exist, add them here.
          identifyUser(customerId);
        }
      } else if (isUnauthorized(error)) {
        // The session is gone: reset a person PostHog still holds from earlier -
        // this page load or a previous one - so what follows is not filed under
        // them. Only a 401 says that. A 5xx or a dropped connection says nothing
        // about the session, and resetting on it would split a still-logged-in
        // person in two. `resetIdentity` leaves an anonymous visitor alone, which
        // keeps their pre-login browsing stitchable, and does nothing when logout
        // already reset, so the anonymous id rotates once.
        identifiedIdRef.current = null;
        resetIdentity();
      }
    }

    holdPageviewsRef.current = false;
    capturePageview();
  }, [customerId, status, error, capturePageview]);

  return <>{children}</>;
};

function isUnauthorized(error: unknown): boolean {
  return isFetchError(error) && error.status === 401;
}
